import React, { useState, useEffect } from "react";
import { Search, RotateCcw, Box, Eye, CornerUpLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

// --- Helper function → Load ONLY archived/deleted ---
const loadArchivedCustomers = () => {
    const all = JSON.parse(localStorage.getItem("customers_list") || "[]");
    return all.filter(c => c.status === "Archived" || c.status === "Deleted");
};

const ArchivePage = () => {
    const navigate = useNavigate(); 
    const [allCustomers, setAllCustomers] = useState(loadArchivedCustomers);
    const [search, setSearch] = useState("");

    // Keep data synced
    useEffect(() => {
        const handleStorageChange = () => {
            setAllCustomers(loadArchivedCustomers());
        };
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    // ---- View Button ----
    const handleView = (customerId) => {
        navigate(`/customers/view/${customerId}`);
    };

    // ---- RESTORE CUSTOMER ----
    const handleRestore = (customerId, currentName) => {
        if (!window.confirm(`Restore "${currentName}" back to active list?`)) {
            return;
        }

        // Load all from storage
        const customers = JSON.parse(localStorage.getItem("customers_list") || "[]");

        const updated = customers.map(c => {
            if (c.id === customerId) {
                return {
                    ...c,
                    status: "Active",
                    expired: c.expiry ? new Date(c.expiry) < new Date() : false
                };
            }
            return c;
        });

        // Save
        localStorage.setItem("customers_list", JSON.stringify(updated));

        // Remove from the archive list immediately
        setAllCustomers(prev => prev.filter(c => c.id !== customerId));

        // Optional: navigate('/customers');
    };

    // ---- SEARCH ----
    const filteredCustomers = allCustomers.filter(c =>
        Object.values(c).some(val =>
            String(val).toLowerCase().includes(search.toLowerCase())
        )
    );

    const totalRecords = allCustomers.length;

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <Box size={20} /> Archived & Deleted Records ({totalRecords})
                </h1>

                <button
                    onClick={() => setAllCustomers(loadArchivedCustomers())}
                    className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 py-2 rounded shadow-sm hover:bg-gray-50 transition duration-150"
                >
                    <RotateCcw size={16} />
                    <span>Refresh Data</span>
                </button>
            </div>

            <blockquote className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-3 mb-6 text-sm italic">
                This page shows only <b>archived</b> and <b>deleted</b> records.
                Use the <b>Restore</b> button to return a customer to the active list.
            </blockquote>

            {/* Search */}
            <div className="relative mb-6">
                <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                <input
                    type="search"
                    placeholder="Search archived/deleted records..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none"
                />
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {['Customer Name', 'Policy Number', 'Vehicle', 'Status', 'Expiry Date', 'Actions'].map(header => (
                                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCustomers.map((customer, index) => (
                            <tr key={index} className="bg-gray-100 hover:bg-gray-200">
                                <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900">{customer.name}</div>
                                    <div className="text-xs text-gray-500">{customer.id}</div>
                                </td>

                                <td className="px-4 py-3 text-gray-600">{customer.policy}</td>

                                <td className="px-4 py-3">
                                    <div className="text-gray-900">{customer.vehicle}</div>
                                    <div className="text-xs text-gray-500">{customer.reg}</div>
                                </td>

                                <td className="px-4 py-3">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                        {customer.status}
                                    </span>
                                </td>

                                <td className={`px-4 py-3 font-bold ${customer.expired ? "text-red-600" : "text-gray-700"}`}>
                                    {customer.expiry}
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap text-right space-x-2">
                                    {/* Restore */}
                                    <button
                                        onClick={() => handleRestore(customer.id, customer.name)}
                                        className="text-green-600 hover:text-green-800 p-1 border rounded"
                                        title="Restore"
                                    >
                                        <CornerUpLeft className="w-5 h-5" />
                                    </button>

                                    {/* View */}
                                    <button
                                        onClick={() => handleView(customer.id)}
                                        className="text-gray-600 hover:text-blue-600 p-1 border rounded"
                                        title="View"
                                    >
                                        <Eye className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 text-sm text-gray-500">
                Showing {filteredCustomers.length} of {totalRecords} archived records.
            </div>
        </div>
    );
};

export default ArchivePage;
